import type { ReactNode } from "react";
import clsx from "clsx";
import Tooltip, { type TooltipProps } from "@mui/material/Tooltip";
import { useCan } from "./hooks/useCan";
import type { ISpacePermissaoTipo } from "@/api/space/dtos/authenticator.dto";

// --------------------------------- TYPES ---------------------------------
type CanModo = "esconder" | "desabilitar";

export type CanEstado = {
	readonly permitido: boolean;
	readonly carregando: boolean;
	readonly negado?: boolean;
};

type CanProps = {
	readonly tag: string;
	readonly children: ReactNode;
	readonly modo?: CanModo; // default "esconder"
	readonly tipoGrupo: ISpacePermissaoTipo;
	readonly tooltip?: string; // mensagem no modo "desabilitar"
	readonly tooltipPlacement?: TooltipProps["placement"];
	readonly fallback?: ReactNode; // o que mostrar quando negado (modo "esconder")
	readonly aoCarregar?: ReactNode; // o que mostrar enquanto verifica (modo "esconder")
	readonly className?: string; // classes do wrapper no modo "desabilitar"
	/** Reutiliza uma permissão já consultada pelo container, sem repetir a chamada ao backend. */
	readonly estado?: CanEstado;
};

type CanResolvidoProps = CanProps &
	CanEstado & {
		readonly negado: boolean;
	};

// -------------------------------- HELPERS --------------------------------
function obterEstadoNegado(estado: CanEstado | undefined, negadoConsultado: boolean): boolean {
	if (!estado) return negadoConsultado;
	if (estado.negado !== undefined) return estado.negado;
	return !estado.permitido && !estado.carregando;
}

// ------------------------------- COMPONENTS ------------------------------
function CanResolvido({
	children,
	modo = "esconder",
	tooltip,
	tooltipPlacement = "top",
	fallback = null,
	aoCarregar = null,
	className,
	permitido,
	carregando,
	negado,
}: Readonly<CanResolvidoProps>) {
	if (modo === "desabilitar") {
		if (permitido) return children;

		const travado = (
			<span aria-disabled className={clsx("inline-flex cursor-not-allowed opacity-50", className)}>
				<span inert>{children}</span>
			</span>
		);

		return tooltip ? (
			<Tooltip placement={tooltipPlacement} arrow title={tooltip}>
				{travado}
			</Tooltip>
		) : (
			travado
		);
	}

	// modo "esconder"
	if (carregando) return aoCarregar;
	if (negado) return fallback;
	return permitido ? children : fallback;
}

function CanConsultado(props: Readonly<CanProps>) {
	const permissao = useCan(props.tag, props.tipoGrupo);

	return <CanResolvido {...props} {...permissao} />;
}

/**
 * Gateia elementos da tela por uma `tag` de permissão, decidindo no front-end se o
 * conteúdo fica escondido (`modo="esconder"`) ou desabilitado (`modo="desabilitar"`,
 * opcionalmente com `tooltip`). Reaproveita `useCan` (e, por baixo, `useVerificaPermissao`)
 * ou consome o `estado` já resolvido por um container.
 */
// ------------------------------- COMPONENT -------------------------------
export default function Can(props: Readonly<CanProps>) {
	if (!props.estado) return <CanConsultado {...props} />;

	return (
		<CanResolvido
			{...props}
			permitido={props.estado.permitido}
			carregando={props.estado.carregando}
			negado={obterEstadoNegado(props.estado, false)}
		/>
	);
}
